import { useContext } from 'react'
import s from './AuthorizationModal.module.css'
import { TiSocialGooglePlus } from 'react-icons/ti'
import { useGoogleLogin, CodeResponse } from '@react-oauth/google'
import { useForm } from 'react-hook-form';
import { UserContext } from '../../App';
import { toast } from 'react-toastify'
import { fetchUserRatings, postGoogleAccount, postSignIn, postSignUp } from './AuthorizationService';
import { setClientSideError, setServerSideError } from '../../utility/ErrorHelper';
import { COMMON, EMAIL, PASSWORD, SUBMIT, TEXT, TOKEN } from '../../utility/Constants';
import { getConfirmPasswordErrorText, getConfirmPasswordText, getEmailErrorText, getEmailText, getFirstNameErrorText, getFirstNameText, getLastNameErrorText, getLastNameText, getPasswordErrorText, getPasswordText } from './translatedText/translatedText';
import { UserContextInterface } from '../../utility/commonTypes';
import AuthorizationInput from './authorizationInput/AuthorizationInput';

const AuthorizationModal = ({ isLoginModal, isSignUpModal, setIsLoginModal, setIsSignUpModal }: Props) => {
    const { setUser, language } = useContext<UserContextInterface>(UserContext)
    const { register, handleSubmit, reset, clearErrors, setError, formState: { errors } } = useForm()

    const commonErrorMessage = typeof errors.common?.message === 'string' ? errors.common.message : undefined
    const googleErrorMessage = typeof errors.google?.message === 'string' ? errors.google.message : undefined

    const submitLogin = async (data: any) => {
        try {
            clearErrors(COMMON)

            await enterUser(true, postSignIn, data)
        } catch (error: any) {
            setServerSideError(setError, error.response?.data?.message[0]?.msg || error.response?.data?.message)
        }
    }
    const submitRegister = async (data: any) => {
        try {
            if (data.password !== data.confirmPassword) return setClientSideError(setError, getConfirmPasswordErrorText(language))

            await enterUser(false, postSignUp, data)
        } catch (error: any) {
            setServerSideError(setError, error.response?.data?.message[0]?.msg || error.response?.data?.message)
        }
    }

    const googleSuccess = async (codeResponse: CodeResponse) => {
        if (!codeResponse.code) {
            toast.error('Authorization has failed. Error: missing authorization code')
            return
        }

        await enterUser(true, postGoogleAccount, { code: codeResponse.code })
    }
    const googleFailure = (error: any) => {
        if (error?.error === 'popup_closed_by_user') return
        toast.error(`Authorization has failed. Error: ${error?.error}`)
    }

    const googleLogin = useGoogleLogin({
        flow: 'auth-code',
        onSuccess: googleSuccess,
        onError: googleFailure,
        scope: 'openid email profile',
        redirect_uri: 'postmessage'
    });

    const enterUser = async (isLogin: boolean, signUser: any, data: any) => {
        const response: any = await signUser(data)
        const userRatingResponse: any = isLogin && await fetchUserRatings(response.data.user._id)

        localStorage.setItem(TOKEN, response.data.accessToken)
        isLogin
            ? setUser({ ...response.data.user, bookRatings: { ...userRatingResponse.data.bookIdToRating } })
            : setUser({ ...response.data.user })

        hidePopup()
    }

    const hidePopup = () => {
        setIsLoginModal(false)
        setIsSignUpModal(false)
        reset()
    }

    return (
        <div onClick={hidePopup} className={isLoginModal || isSignUpModal ? `${s.authorizationModal} ${s.active}` : s.authorizationModal}>
            <div onClick={e => e.stopPropagation()} className={isLoginModal || isSignUpModal ? `${s.authorizationModalContent} ${s.active}` : s.authorizationModalContent}>
                
                <button onClick={hidePopup} className={s.closeButton} type="button" aria-label="Close">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                <div className={s.signUpModalContent}>
                    <div className={s.title} >{isSignUpModal && 'SIGN UP'}
                        {isLoginModal && 'LOGIN'}
                    </div>
                    <form>
                        <div className={s.nameWrapper}>
                            {isSignUpModal && <AuthorizationInput 
                                inputArea='firstName' inputType={TEXT}
                                errors={errors} register={register}
                                placeholder={getFirstNameText(language)}
                                errorText={getFirstNameErrorText(language)}
                                validationObject={{ required: true }}/>
                            }
                            {isSignUpModal && <AuthorizationInput 
                                inputArea='lastName' inputType={TEXT}
                                errors={errors} register={register}
                                placeholder={getLastNameText(language)}
                                errorText={getLastNameErrorText(language)}
                                validationObject={{ required: true }}/>
                            }
                        </div>
                        <AuthorizationInput 
                            inputArea={EMAIL} inputType={EMAIL}
                            errors={errors} register={register}
                            placeholder={getEmailText(language)}
                            errorText={getEmailErrorText(language)}
                            validationObject={{ onChange: () => clearErrors(COMMON), required: true, pattern: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/ }}/>
                        <div className={s.passwordWrapper}>
                            <AuthorizationInput 
                                inputArea={PASSWORD} inputType={PASSWORD}
                                errors={errors} register={register}
                                placeholder={getPasswordText(language)} 
                                errorText={getPasswordErrorText(language)} 
                                validationObject={{ onChange: () => clearErrors(COMMON), required: true, minLength: 6 }}/>
                            {isSignUpModal && <AuthorizationInput 
                                inputArea='confirmPassword' inputType={PASSWORD} 
                                errors={errors} register={register} 
                                placeholder={getConfirmPasswordText(language)} 
                                errorText={getConfirmPasswordErrorText(language)} 
                                validationObject={{ onChange: () => clearErrors(COMMON), required: true }}/>
                            }
                        </div> 
                        {commonErrorMessage && <p className={s.errorText}>{commonErrorMessage}</p>}
                    </form>
                </div>

                {isLoginModal && <button onClick={handleSubmit(submitLogin)} type={SUBMIT} className={s.button}>
                    <span className={s.text}>Login</span>
                </button>}
                {isSignUpModal && <button onClick={handleSubmit(submitRegister)} type={SUBMIT} className={s.button}>
                    <span className={s.text}>Register</span>
                </button>}

                <div className={s.divider}>or</div>

                <button className={s.googltButton} onClick={() => googleLogin()} type="button">
                    <TiSocialGooglePlus className={s.icon} />
                    <span className={s.googleText}>Login with Google</span>
                </button>
                {googleErrorMessage && <p className={s.errorText}>{googleErrorMessage}</p>}
            </div>
        </div>
    )
}

interface Props {
    isLoginModal: boolean
    isSignUpModal: boolean
    setIsLoginModal: any
    setIsSignUpModal: any
}

export default AuthorizationModal